import { FC, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import qs from 'query-string';
import dayjs from 'dayjs';

import { IconArrowLeft, IconLike, IconLiked } from '../../icons';
import Page from '../../components/Page/Page';
import Loader from '../../components/Loader/Loader';
import { iNewsNavigationState } from '../News/News';
import RoutesEnum from '../../core/enums/RoutesEnum';
import { processHtmlLinksForNewTab } from '../../core/helpers';
import useNewsArticleDetails from '../../core/apiHooks/useNewsArticleDetails';
import useRecentNewsArticles from '../../core/apiHooks/useRecentNewsArticles';
import useNewsArticleFeedback from '../../core/apiHooks/useNewsArticleFeedback';

import './NewsArticle.styles.scss';
import NewsArticleReferences from './NewsArticleReferences/NewsArticleReferences';
import DownloadArticlePdfButton from './DownloadArticlePdfButton/DownloadArticlePdfButton';

const HIGHLIGHT_CLASS = 'NewsArticle__content-highlight';

const MAX_SCROLL_ATTEMPTS = 5;
const SCROLL_RETRY_DELAY = 100;

const NewsArticle: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { state, pathname, search, hash } = useLocation();

    const returnUrl = state?.returnUrl || RoutesEnum.News;
    const articleId = Number.parseInt(id!, 10);

    const { data: article, isLoading, isSuccess } = useNewsArticleDetails(articleId);
    const { recentNewsArticles: featuredNewsArticles } = useRecentNewsArticles();
    const { isLiked, toggleLikeFeedback } = useNewsArticleFeedback(articleId);

    const handleBackToNews = useCallback(() => {
        navigate(returnUrl);
    }, [navigate, returnUrl]);

    useEffect(() => {
        if (isSuccess === false) {
            handleBackToNews();
            return;
        }

        if (!isLoading && (!article?.content || !article?.id)) {
            handleBackToNews();
            return;
        }

        if (!isLoading && isSuccess === true) {
            const existingParams = search ? qs.parse(search) : {};

            if (existingParams.loaded === 'true') return;

            const timer = setTimeout(() => {
                const newParams = { ...existingParams, loaded: 'true' };
                const newSearchString = qs.stringify(newParams);
                navigate(`${pathname}?${newSearchString}${hash}`, { replace: true });
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [article?.content, article?.id, handleBackToNews, navigate, isLoading, isSuccess, pathname, search, hash]);

    useEffect(() => {
        if (isLoading || !article?.content || !hash) return;

        const targetId = hash.slice(1); // drop the leading '#'
        if (!targetId) return;

        let attempts = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let highlighted: HTMLElement | null = null;

        const tryScroll = () => {
            const target = document.getElementById(targetId);

            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                target.classList.add(HIGHLIGHT_CLASS);
                highlighted = target;
                return;
            }

            if (attempts++ < MAX_SCROLL_ATTEMPTS) {
                timer = setTimeout(tryScroll, SCROLL_RETRY_DELAY);
            }
        };

        tryScroll();

        return () => {
            clearTimeout(timer);
            highlighted?.classList.remove(HIGHLIGHT_CLASS);
        };
    }, [isLoading, article?.content, hash]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Page title={article?.title || 'News Article'} isResponsive>
            <div className="NewsArticle">
                <div className="NewsArticle__header">
                    <div className="NewsArticle__breadcrumb" onClick={handleBackToNews}>
                        <IconArrowLeft className="Icon16" />
                        <Link to={returnUrl} className="NewsArticle__breadcrumb-text">
                            Back to News Archive
                        </Link>
                    </div>

                    {article?.featuredImage && (
                        <div className="NewsArticle__image">
                            <img src={article.featuredImage} alt={article.title || 'Article featured'} />
                        </div>
                    )}
                </div>

                <div className="NewsArticle__meta">
                    <div className="NewsArticle__title">{article?.title || 'headline goes here'}</div>

                    {article?.publishedDate && (
                        <div className="NewsArticle__info">
                            <span>{article?.publishedDate}</span>
                        </div>
                    )}

                    <div className="NewsArticle__content">
                        {isLoading && (
                            <div className="NewsArticle__content-loading">
                                <Loader />
                            </div>
                        )}

                        {!isLoading && article?.content && article.content.length > 0 && (
                            <div
                                className="NewsArticle__content-html"
                                dangerouslySetInnerHTML={{
                                    __html: processHtmlLinksForNewTab(article.content)
                                }}
                            />
                        )}
                    </div>

                    <div className="NewsArticle__footer">
                        <div className="NewsArticle__tags">
                            {!isLoading && article?.tags && article.tags.length > 0 && (
                                <>
                                    <div className="NewsArticle__tags-label">news tags</div>
                                    <div className="NewsArticle__tags-list">
                                        {article.tags.map((tag) => (
                                            <div key={tag} className="NewsArticle__tags-item">
                                                {tag}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="NewsArticle__feedback">
                            <div
                                className={'NewsArticle__feedback-icon'}
                                data-testid="buttonLikeArticle"
                                onClick={() => toggleLikeFeedback(articleId, isLiked === true ? null : true)}
                            >
                                {isLiked === true ? <IconLiked className="Icon32" /> : <IconLike className="Icon32" />}
                            </div>
                            <div
                                className="NewsArticle__feedback-icon is-dislike-icon"
                                data-testid="buttonDislikeArticle"
                                onClick={() => toggleLikeFeedback(articleId, isLiked === false ? null : false)}
                            >
                                {isLiked === false ? <IconLiked className="Icon32" /> : <IconLike className="Icon32" />}
                            </div>
                            <DownloadArticlePdfButton
                                articleId={articleId}
                                articleTitle={article?.title}
                                className="NewsArticle__feedback-icon"
                            />
                        </div>
                    </div>

                    <div className="NewsArticle__recent">
                        <div className="NewsArticle__recent-wrapper">
                            <div className="NewsArticle__recent-container">
                                <div className="NewsArticle__recent-header">
                                    <h3 className="NewsArticle__recent-heading">most recent articles</h3>
                                </div>

                                <div className="NewsArticle__recent-content">
                                    <div className="NewsArticle__recent-articles">
                                        {featuredNewsArticles.slice(0, 3).map((recentArticle) => (
                                            <Link
                                                key={recentArticle.id}
                                                to={RoutesEnum.NewsArticle.replace(':id', String(recentArticle.id))}
                                                className="NewsArticle__recent-article"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <div className="NewsArticle__recent-article-title">{recentArticle.title}</div>
                                                {recentArticle.publishedDate && dayjs(recentArticle.publishedDate).isValid() && (
                                                    <div className="NewsArticle__recent-article-date">
                                                        {dayjs(recentArticle.publishedDate).format('MMMM D, YYYY')}
                                                    </div>
                                                )}
                                            </Link>
                                        ))}
                                    </div>

                                    <div className="NewsArticle__recent-archive">
                                        <Link to={returnUrl} className="NewsArticle__recent-archive-link">
                                            news archive
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <NewsArticleReferences articleId={articleId} />
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default NewsArticle;
